<template>
  <div class="main-lobby">
    <div class="content-wrapper">
      <h1 class="gradient-text">Welcome to Dashamajig!</h1>
      <h2 class="gradient-text">You are privy to the most exclusive of insights, man.</h2>

      <!-- Tab Buttons -->
      <div class="tabs">
        <button
            :class="{ active: activeTab === 'orders' }"
            @click="activeTab = 'orders'"
        >
          Orders
        </button>
        <button
            :class="{ active: activeTab === 'products' }"
            @click="activeTab = 'products'"
        >
          Products
        </button>
      </div>

      <!-- Product Filter Options -->
      <div v-if="activeTab === 'products'" class="filter-section">
        <label>
          <input type="radio" value="active" v-model="productFilter" />
          Active Products
        </label>
        <label>
          <input type="radio" value="all" v-model="productFilter" />
          All Products
        </label>
        <!-- Date filters appear only when "all" is selected -->
        <div v-if="productFilter === 'all'" class="date-filters">
          <label>
            Month:
            <select v-model="selectedMonth">
              <option value="">All</option>
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
          </label>
          <label>
            Day:
            <select v-model="selectedDay">
              <option value="">All</option>
              <option v-for="d in days" :key="d" :value="d">{{ d }}</option>
            </select>
          </label>
          <label>
            Year:
            <select v-model="selectedYear">
              <option value="">All</option>
              <option v-for="y in availableYears" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
          </label>
          <p class="date-instruction">
            (Show products launched on or before the selected date)
          </p>
        </div>
        <!-- Report Type Filters -->
        <div class="report-type-filters">
          <label>
            <input type="radio" value="full" v-model="productReportMode" />
            Full Report
          </label>
          <label>
            <input type="radio" value="summary" v-model="productReportMode" />
            Summary Report
          </label>
          <label>
            <input type="radio" value="custom" v-model="productReportMode" />
            Custom Report
          </label>
        </div>
        <!-- Custom Fields Checkboxes -->
        <div v-if="isCustomReport" class="custom-fields">
          <label v-for="field in productFieldsList" :key="field.key">
            <input type="checkbox" v-model="field.selected" />
            {{ field.label }}
          </label>
        </div>
      </div>

      <!-- Fetch & Download Buttons -->
      <p
          v-if="activeTab === 'orders' && !orders.length && !ordersLoading"
          class="orders-get-started-hint"
      >
        To get started, click Fetch Orders below.
      </p>
      <div class="fetch-buttons">
        <button
            v-if="activeTab === 'orders'"
            @click="fetchData"
            class="fetch-button"
        >
          {{ loadButtonText }}
        </button>
        <button
            v-if="activeTab === 'products'"
            @click="fetchData"
            class="fetch-button"
        >
          {{ loadProductsButtonText }}
        </button>
        <button
            @click="downloadTSV"
            class="fetch-button"
            :disabled="!hasTableData"
        >
          Download TSV
        </button>
      </div>

      <!-- Orders Section -->
      <div
          v-if="activeTab === 'orders'"
      >
        <p v-if="ordersLoading" class="loading-message">Woof! Fetching orders…</p>
        <p v-else-if="orders.length">{{ orders.length }} orders loaded</p>
        <div class="orders-filters">
          <input
              type="text"
              v-model="searchQuery"
              placeholder="Search orders..."
              class="search-bar"
          />
          <div class="orders-tag-filter">
            <input
                type="text"
                v-model.trim="tagQuery"
                placeholder="Filter by customer tag..."
                class="search-bar"
                @keydown.enter.prevent="applyTagSuggestionOnEnter"
                @focus="searchTags"
                @blur="hideTagSuggestions"
            />
            <ul v-if="tagSuggestions.length" class="orders-tag-suggestions">
              <li
                  v-for="tag in tagSuggestions"
                  :key="tag"
                  @mousedown.prevent="selectTagSuggestion(tag)"
              >
                {{ tag }}
              </li>
            </ul>
          </div>
        </div>
        <div class="table-container">
          <div class="table-responsive">
            <table class="table orders-table">
              <thead>
              <tr>
                <th @click="sortTable('date_added')" class="sortable">
                  Date Placed
                </th>
                <th @click="sortTable('date_shipped')" class="sortable">
                  Date Shipped
                </th>
                <th @click="sortTable('order_number')" class="sortable">
                  Order Number
                </th>
                <th @click="sortTable('customer_tags_text')" class="sortable">
                  Customer Tags
                </th>
                <th @click="sortTable('shipping_name')" class="sortable">
                  Deliver to
                </th>
                <th @click="sortTable('shipping_company')" class="sortable">
                  Org
                </th>
                <th @click="sortTable('shipping_address')" class="sortable">
                  Shipping Address
                </th>
                <th>Item Details</th>
                <th @click="sortTable('ship_charge_total')" class="sortable">
                  Total Shipping Cost
                </th>
                <th @click="sortTable('retail_value')" class="sortable">
                  Retail Value
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="order in filteredOrders" :key="order.id">
                <td>{{ order.date_added }}</td>
                <td>{{ order.date_shipped }}</td>
                <td>
                  <router-link
                    v-if="order.customer_id"
                    class="order-contact-link"
                    :to="{
                      name: 'CustomerDetail',
                      params: { id: order.customer_id },
                      query: { tab: 'orders', order: String(order.order_number || '') }
                    }"
                    :title="`Open contact and show order ${order.order_number}`"
                  >{{ order.order_number }}</router-link>
                  <span
                    v-else
                    :title="order.customer_email ? 'No matching contact for this email' : 'No contact email on this order'"
                  >{{ order.order_number }}</span>
                </td>
                <td>{{ order.customer_tags_text || 'N/A' }}</td>
                <td>{{ order.shipping_name || '—' }}</td>
                <td>{{ order.shipping_company || '—' }}</td>
                <td class="ship-address">{{ order.shipping_address }}</td>
                <td>
                  <ul>
                    <li
                        v-for="item in order.items"
                        :key="item.sku"
                    >
                      {{ item.quantity }}x {{ item.description }} (SKU:
                      {{ item.sku }}) - ${{ item.retail_value }}
                    </li>
                  </ul>
                </td>
                <td>${{ order.ship_charge_total }}</td>
                <td>{{ order.retail_value.toFixed(2) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Products Section -->
      <div
          v-if="activeTab === 'products' && products.length > 0"
      >
        <p>{{ products.length }} products loaded</p>
        <input
            type="text"
            v-model="searchQuery"
            placeholder="Search products..."
            class="search-bar"
        />
        <div class="table-container">
          <div class="table-responsive">
            <table class="table products-table">
              <thead>
              <!-- Full Report -->
              <tr v-if="isFullReport">
                <th>SKU</th>
                <th>ProductCat</th>
                <th>Description</th>
                <th>WholesaleValue</th>
                <th>RetailValue</th>
                <th>MaxShelfBoxResolved</th>
                <th>locQuantity</th>
                <th>OnHand</th>
                <th>Available</th>
                <th>Committed</th>
                <th>Pulled</th>
                <th>BackOrdered</th>
                <th>OnAsn</th>
                <th>HasBattery</th>
                <th>CanReceive</th>
                <th>AddDate</th>
                <th>LastReceived</th>
                <th>Approved</th>
                <th>SkuAlias</th>
                <th>Weight</th>
                <th>Length</th>
                <th>Width</th>
                <th>Height</th>
              </tr>
              <!-- Summary Report -->
              <tr v-else-if="isSummaryReport">
                <th>SKU</th>
                <th>Description</th>
                <th>OnHand</th>
                <th>Available</th>
                <th>Committed</th>
                <th>Pulled</th>
                <th>BackOrdered</th>
                <th>OnAsn</th>
                <th>AddDate</th>
                <th>LastReceived</th>
              </tr>
              <!-- Custom Report -->
              <tr v-else-if="isCustomReport">
                <th
                    v-for="field in selectedProductFields"
                    :key="field.key"
                >
                  {{ field.label }}
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="product in filteredProducts" :key="product.sku">
                <!-- Full -->
                <template v-if="isFullReport">
                  <td>{{ product.sku }}</td>
                  <td>{{ product.product_cat }}</td>
                  <td>{{ product.description }}</td>
                  <td>${{ product.wholesale_value }}</td>
                  <td>${{ product.retail_value }}</td>
                  <td>{{ product.max_shelf_box_resolved }}</td>
                  <td>{{ product.loc_quantity }}</td>
                  <td>{{ product.on_hand }}</td>
                  <td>{{ product.available }}</td>
                  <td>{{ product.committed }}</td>
                  <td>{{ product.pulled }}</td>
                  <td>{{ product.back_ordered }}</td>
                  <td>{{ product.on_asn }}</td>
                  <td>{{ product.has_battery }}</td>
                  <td>{{ product.can_receive }}</td>
                  <td>{{ formatDate(product.add_date) }}</td>
                  <td>{{ formatDate(product.last_received) }}</td>
                  <td>{{ product.approved }}</td>
                  <td>{{ product.sku_alias }}</td>
                  <td>{{ product.weight }}</td>
                  <td>{{ product.length }}</td>
                  <td>{{ product.width }}</td>
                  <td>{{ product.height }}</td>
                </template>
                <!-- Summary -->
                <template v-else-if="isSummaryReport">
                  <td>{{ product.sku }}</td>
                  <td>{{ product.description }}</td>
                  <td>{{ product.on_hand }}</td>
                  <td>{{ product.available }}</td>
                  <td>{{ product.committed }}</td>
                  <td>{{ product.pulled }}</td>
                  <td>{{ product.back_ordered }}</td>
                  <td>{{ product.on_asn }}</td>
                  <td>{{ formatDate(product.add_date) }}</td>
                  <td>{{ formatDate(product.last_received) }}</td>
                </template>
                <!-- Custom -->
                <template v-else-if="isCustomReport">
                  <td
                      v-for="field in selectedProductFields"
                      :key="field.key"
                  >
                      <span v-if="['wholesale_value','retail_value'].includes(field.key)">
                        ${{ product[field.key] }}
                      </span>
                    <span v-else-if="['add_date','last_received'].includes(field.key)">
                        {{ formatDate(product[field.key]) }}
                      </span>
                    <span v-else>
                        {{ product[field.key] }}
                      </span>
                  </td>
                </template>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FulfillriteService from '@/services/FulfillriteService';
import TagsService from '@/services/TagsService';

export default {
  name: 'MainLobby',
  props: {
    username: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      activeTab: 'orders',
      orders: [],
      products: [],
      productFilter: 'active', // "active" or "all"
      productReportMode: 'full', // "full", "summary" or "custom"
      selectedMonth: "",
      selectedDay: "",
      selectedYear: "",
      currentSortField: 'date_added',
      currentSortDir: -1,
      productSortField: 'sku',
      productSortDir: 1,
      searchQuery: '',
      tagQuery: '',
      tagSearchTimer: null,
      tagSuggestionTimer: null,
      tagSuggestions: [],
      tagSelectionInProgress: false,
      page: 1,
      pageSize: 100,
      hasMoreOrders: true,
      loadButtonText: 'Fetch Orders',
      ordersLoading: false,
      loadProductsButtonText: 'Fetch Products',
      // Customizable product fields
      productFields: [
        { key: 'sku', label: 'SKU', selected: true },
        { key: 'product_cat', label: 'ProductCat', selected: true },
        { key: 'description', label: 'Description', selected: true },
        { key: 'wholesale_value', label: 'WholesaleValue', selected: true },
        { key: 'retail_value', label: 'RetailValue', selected: true },
        { key: 'add_date', label: 'AddDate', selected: true },
        { key: 'last_received', label: 'LastReceived', selected: true },
        { key: 'on_hand', label: 'OnHand', selected: true },
        { key: 'available', label: 'Available', selected: true },
        { key: 'committed', label: 'Committed', selected: true },
        { key: 'pulled', label: 'Pulled', selected: true },
        { key: 'back_ordered', label: 'BackOrdered', selected: true },
        { key: 'on_asn', label: 'OnAsn', selected: true },
        { key: 'weight', label: 'Weight', selected: true },
        { key: 'length', label: 'Length', selected: true },
        { key: 'width', label: 'Width', selected: true },
        { key: 'height', label: 'Height', selected: true }
      ]
    };
  },
  computed: {
    userRole() {
      const token = localStorage.getItem("token");
      if (!token) return null;
      try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4 !== 0) payload += '=';
        const decoded = JSON.parse(atob(payload));
        return decoded ? decoded.role : null;
      } catch (e) {
        console.error('Error decoding JWT:', e);
        return null;
      }
    },
    isFullReport() {
      return this.productReportMode === 'full';
    },
    isSummaryReport() {
      return this.productReportMode === 'summary';
    },
    isCustomReport() {
      return this.productReportMode === 'custom';
    },
    productFieldsList() {
      return this.productFields;
    },
    selectedProductFields() {
      return this.productFields.filter(f => f.selected);
    },
    hasTableData() {
      return (
          (this.activeTab === 'orders' && this.orders.length > 0) ||
          (this.activeTab === 'products' && this.products.length > 0)
      );
    },
    selectedTimeMachineDate() {
      if (this.selectedMonth && this.selectedDay && this.selectedYear) {
        const m = String(this.selectedMonth).padStart(2, '0');
        const d = String(this.selectedDay).padStart(2, '0');
        return new Date(`${this.selectedYear}-${m}-${
            d
        }`);
      }
      return null;
    },
    filteredOrders() {
      if (!this.orders) return [];
      const q = this.searchQuery.toLowerCase();
      const filtered = this.orders.filter(order => {
        const directMatch = Object.entries(order).some(([k, v]) => {
          if (k === 'items' || k.endsWith('_raw') || v == null) return false;
          return v.toString().toLowerCase().includes(q);
        });
        const itemMatch = order.items.some(item => {
          return Object.values(item).some(
              v => v != null && v.toString().toLowerCase().includes(q)
          );
        });
        return directMatch || itemMatch;
      });
      const field = this.currentSortField;
      const dir = this.currentSortDir;
      return filtered.sort((a, b) => {
        let A = a[field];
        let B = b[field];
        if (field === 'date_added' || field === 'date_shipped') {
          A = new Date(a[`${field}_raw`] || A).getTime();
          B = new Date(b[`${field}_raw`] || B).getTime();
        } else if (field === 'retail_value' || field === 'ship_charge_total') {
          A = Number(A) || 0;
          B = Number(B) || 0;
        }
        if (A == null || B == null || Number.isNaN(A) || Number.isNaN(B)) return 0;
        if (A === B) return 0;
        return (A < B ? -1 : 1) * dir;
      });
    },
    filteredProducts() {
      if (!this.products) return [];
      let filtered = this.products.filter(p => {
        return Object.values(p).some(
            v =>
                v != null &&
                v
                    .toString()
                    .toLowerCase()
                    .includes(this.searchQuery.toLowerCase())
        );
      });
      const tm = this.selectedTimeMachineDate;
      if (this.productFilter === 'all' && tm) {
        filtered = filtered.filter(
            p => p.add_date && new Date(p.add_date) <= tm
        );
      }
      return filtered.sort(
          (a, b) => new Date(a.add_date) - new Date(b.add_date)
      );
    },
    availableYears() {
      const cy = new Date().getFullYear(),
          years = [];
      for (let y = 2015; y <= cy; y++) years.push(y);
      return years;
    },
    months() {
      return [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
      ];
    },
    days() {
      return Array.from({ length: 31 }, (_, i) => String(i + 1));
    }
  },
  created() {
    if (this.userRole === 'standard') this.activeTab = 'products';
  },
  beforeUnmount() {
    clearTimeout(this.tagSearchTimer);
    clearTimeout(this.tagSuggestionTimer);
  },
  watch: {
    tagQuery() {
      if (this.tagSelectionInProgress) {
        this.tagSelectionInProgress = false;
        return;
      }
      this.scheduleTagSearch();
      this.scheduleOrdersReload();
    }
  },
  methods: {
    fetchData() {
      if (this.activeTab === 'orders') this.fetchOrders();
      else this.fetchProducts();
    },
    fetchOrders() {
      if (!this.hasMoreOrders) return;
      this.ordersLoading = true;
      FulfillriteService.getOrders(
          this.page,
          this.pageSize,
          this.productFilter,
          this.tagQuery
      )
          .then(resp => {
            const newO = resp.data.map(o => ({
              ...o,
              customer_tags: Array.isArray(o.customer_tags) ? o.customer_tags : [],
              customer_tags_text: this.formatTags(o.customer_tags),
              date_added_raw: o.date_added,
              date_shipped_raw: o.date_shipped,
              date_added: o.date_added
                  ? this.formatDate(o.date_added)
                  : 'N/A',
              date_shipped: o.date_shipped
                  ? this.formatDate(o.date_shipped)
                  : 'N/A',
              retail_value: o.items.reduce(
                  (s, i) => s + i.retail_value * i.quantity,
                  0
              )
            }));
      const unique = newO.filter(
                o => !this.orders.some(e => e.id === o.id)
            );
            if (unique.length < this.pageSize) {
              this.hasMoreOrders = false;
              this.loadButtonText = 'No More Orders';
            } else {
              this.loadButtonText = 'Load More';
            }
            this.orders.push(...unique);
            this.page++;
          })
          .catch(e => console.error('Error fetching orders:', e.response))
          .finally(() => { this.ordersLoading = false });
    },
    scheduleTagSearch() {
      clearTimeout(this.tagSuggestionTimer);
      if (!this.tagQuery) {
        this.tagSuggestions = [];
        return;
      }
      this.tagSuggestionTimer = setTimeout(this.searchTags, 150);
    },
    searchTags() {
      const q = this.tagQuery.trim();
      if (!q) {
        this.tagSuggestions = [];
        return;
      }
      TagsService.search(q)
          .then(({ data }) => {
            this.tagSuggestions = data
                .map(t => (typeof t === 'string' ? t : t && t.name))
                .filter(Boolean)
                .map(t => String(t).trim())
                .filter(Boolean)
                .slice(0, 10);
          })
          .catch(() => { this.tagSuggestions = [] });
    },
    hideTagSuggestions() {
      setTimeout(() => { this.tagSuggestions = [] }, 120);
    },
    selectTagSuggestion(tag) {
      this.tagSelectionInProgress = true;
      this.tagQuery = tag;
      this.tagSuggestions = [];
      clearTimeout(this.tagSearchTimer);
      this.resetOrders();
      this.fetchOrders();
    },
    applyTagSuggestionOnEnter() {
      if (this.tagSuggestions.length === 1) {
        this.selectTagSuggestion(this.tagSuggestions[0]);
      } else {
        this.tagSuggestions = [];
        clearTimeout(this.tagSearchTimer);
        this.resetOrders();
        this.fetchOrders();
      }
    },
    scheduleOrdersReload() {
      clearTimeout(this.tagSearchTimer);
      if (this.activeTab !== 'orders') return;
      this.tagSearchTimer = setTimeout(() => {
        this.resetOrders();
        this.fetchOrders();
      }, 350);
    },
    resetOrders() {
      this.orders = [];
      this.page = 1;
      this.hasMoreOrders = true;
      this.loadButtonText = 'Fetch Orders';
    },
    formatTags(tags) {
      return Array.from(new Set(Array(tags).flat()))
          .map(t => String(t).trim())
          .filter(Boolean)
          .join(', ');
    },
    fetchProducts() {
      FulfillriteService.getProducts(this.productFilter)
          .then(resp => (this.products = resp.data))
          .catch(e => console.error('Error fetching products:', e.response));
    },
    formatDate(str) {
      if (!str) return 'N/A';
      const d = new Date(str);
      if (isNaN(d)) return 'N/A';
      const m = d.getMonth() + 1,
          day = d.getDate(),
          yy = d.getFullYear() % 100;
      return `${m}/${day}/${yy.toString().padStart(2, '0')}`;
    },
    sortTable(field) {
      if (this.activeTab !== 'orders') return;
      if (this.currentSortField === field) {
        this.currentSortDir *= -1;
      } else {
        this.currentSortField = field;
        this.currentSortDir = (field === 'date_added' || field === 'date_shipped') ? -1 : 1;
      }
    },
    downloadTSV() {
      const table = this.$el.querySelector(
          '.table-responsive table'
      );
      if (!table) {
        alert('No table found to export!');
        return;
      }
      const rows = Array.from(table.querySelectorAll('tr'));
      const tsv = rows
          .map(r =>
              Array.from(r.querySelectorAll('th, td'))
                  .map(c => {
                    const txt = c.textContent
                        .trim()
                        .replace(/"/g, '""');
                    return txt.includes('\t') || txt.includes('"')
                        ? `"${txt}"`
                        : txt;
                  })
                  .join('\t')
          )
          .join('\n');
      const blob = new Blob([tsv], {
        type: 'text/tab-separated-values;charset=utf-8;'
      });
      const filename = `${this.activeTab}_report.tsv`;
      if (navigator.msSaveBlob) navigator.msSaveBlob(blob, filename);
      else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }
};
</script>

<style scoped>

.orders-get-started-hint {
  margin: 0.35rem 0 0.9rem;
  font-size: 1.08rem;
  font-weight: 500;
  letter-spacing: 0.015em;
  line-height: 1.45;
  background: linear-gradient(
    90deg,
    #fff7ed 0%,
    #fde68a 14%,
    #f9a8d4 32%,
    #c4b5fd 52%,
    #94a3b8 72%,
    #475569 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Date Filters */
.date-filters {
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.date-filters label {
  margin-right: 1rem;
  font-size: 0.9rem;
}

.loading-message {
  font-size: 1rem;
  font-style: italic;
  color: #fbd38d;
  margin: 0.25rem 0 0.75rem;
}

.orders-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  align-items: flex-start;
  margin: 0.35rem auto 0.75rem;
  max-width: 720px;
}

.orders-filters > .search-bar,
.orders-filters > .orders-tag-filter {
  flex: 1 1 280px;
  min-width: 240px;
}

.orders-filters .search-bar {
  width: 100%;
}

.orders-tag-filter {
  position: relative;
}

.orders-tag-suggestions {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% - 0.5rem);
  z-index: 20;
  max-height: 180px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  background: #111;
  border: 1px solid #444;
  border-top: none;
}

.orders-tag-suggestions li {
  padding: 6px 10px;
  color: #ddd;
  cursor: pointer;
}

.orders-tag-suggestions li:hover {
  background: #333;
  color: #fff;
}

/* Report Type Filters */
.report-type-filters {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
}
.report-type-filters label {
  margin-right: 1rem;
  font-size: 0.9rem;
}

/* Custom Fields Checkboxes */
.custom-fields {
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
}
.custom-fields label {
  margin-right: 1rem;
  font-size: 0.9rem;
}
.ship-address {
  white-space: pre-wrap;
  max-width: 18rem;
}
.order-contact-link {
  color: #a5f3fc;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  border-bottom: 1px dashed rgba(165, 243, 252, 0.45);
}
.order-contact-link:hover {
  color: #fff;
  border-bottom-color: #fff;
}
</style>
